import React from 'react'

import { CalendarIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { ExternalCalendarDateRange } from '~/client/src/desktop/enums/ExternalCalendarDateRange'
import { ExternalCalendarDateRangeCommon } from '~/client/src/desktop/enums/ExternalCalendarDateRangeCommon'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Calendar } from '~/client/src/shared/components/shadcn-ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import CalendarLinkInputStore, {
  DateEndOption,
} from '../CalendarLinkInput.store'

interface IProps {
  store: CalendarLinkInputStore
  openFrom: ExternalCalendarDateRange
  openTo: ExternalCalendarDateRange
  from: string
  to: string
  endOption: DateEndOption
  handler: (key: ExternalCalendarDateRangeCommon, date: string) => void
  projectDateStore?: ProjectDateStore
}

const START_MONTH = new Date(1990, 0)
const YEARS_AHEAD = 10

@inject('projectDateStore')
@observer
export default class CalendarDatePicker extends React.Component<IProps> {
  private get store(): CalendarLinkInputStore {
    return this.props.store
  }

  public render(): JSX.Element {
    const { openFrom, openTo, from, to } = this.props
    const isDisabled = this.props.endOption !== DateEndOption.Custom

    return (
      <div className="tw:mt-1 tw:flex tw:gap-2.5 tw:pl-14">
        {this.renderDatePicker(
          openFrom,
          ExternalCalendarDateRangeCommon.FROM,
          from,
        )}
        {this.renderDatePicker(
          openTo,
          ExternalCalendarDateRangeCommon.TO,
          to,
          isDisabled,
        )}
      </div>
    )
  }

  private renderDatePicker(
    openTo: ExternalCalendarDateRange,
    label: ExternalCalendarDateRangeCommon,
    date: string,
    isDisabled: boolean = false,
  ): JSX.Element {
    const { getMonthDayAndYearToDisplay } = this.props.projectDateStore
    const { viewingDate, setViewingDate, setIsOpen } = this.store
    const currentDate = new Date(viewingDate || date || new Date())
    const selectedDate = date ? new Date(date) : undefined
    const endMonth = new Date(new Date().getFullYear() + YEARS_AHEAD, 11)

    return (
      <div>
        <div className="tw:text-label-md tw:text-muted-foreground">
          {Localization.translator[label.toLowerCase()]}
        </div>
        <Popover
          open={this.store.openTo === openTo}
          onOpenChange={isOpen => {
            if (isOpen) {
              setIsOpen(openTo)
            } else if (this.store.openTo === openTo) {
              setIsOpen(ExternalCalendarDateRange.NONE)
            }
          }}
        >
          <PopoverTrigger
            disabled={isDisabled}
            render={<Button variant="outline" />}
          >
            {date
              ? getMonthDayAndYearToDisplay(selectedDate)
              : Localization.translator.chooseDate}
            <CalendarIcon data-icon="inline-end" />
          </PopoverTrigger>
          <PopoverContent align="start" className="tw:w-auto tw:p-0">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={selected => this.selectDate(selected, label)}
              month={currentDate}
              onMonthChange={setViewingDate}
              startMonth={START_MONTH}
              endMonth={endMonth}
            />
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  private selectDate(date: Date, label: ExternalCalendarDateRangeCommon): void {
    if (!date) {
      return
    }

    const {
      handler,
      projectDateStore: { convertToProjectDate },
    } = this.props
    handler(label, convertToProjectDate(date).toISOString())
    this.store.setIsOpen(ExternalCalendarDateRange.NONE)
  }
}
